import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import { localISODate } from '../utils/dates';
import { errorMessage, num, period, shortAmount, PERIODS, monthLabel, downloadCsv } from '../utils/reportHelpers';
import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';

const COLORS = {
  light: { ordered: '#adb5bd', net: '#0d6efd', grid: '#e9ecef', axis: '#6c757d' },
  dark: { ordered: '#6c757d', net: '#3d8bfd', grid: '#3a3a38', axis: '#adb5bd' }
};

/**
 * Online Store > Reports: what the store was ordered, invoiced and sent back in a period, by day (month for a long period), payment method, product and
 * delivery state. Sales are the issued tax invoices of online orders, before GST, less the credit notes of returns; the same figures as Sales > Reports.
 */
function StoreReports() {
  const { theme } = useTheme();
  const colors = COLORS[theme] || COLORS.light;
  const [preset, setPreset] = useState('thisMonth');
  const [range, setRange] = useState(() => period('thisMonth').map(localISODate));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('products');
  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
    return (v) => f.format(num(v));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await axios.get('/api/store/reports', { params: { from: range[0], to: range[1] } });
      setData(r);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not work out the report'));
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const choose = (key) => { setPreset(key); setRange(period(key).map(localISODate)); };
  const o = data?.ordered;
  const s = data?.sales;
  const rows = data ? data[view] : [];
  const label = (k) => (data?.grouping === 'month' ? monthLabel(k) : formatDate(k));
  const share = (v) => (v == null ? '-' : `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`);

  const exportRows = () => {
    const title = { products: 'Product', methods: 'Payment method', states: 'Delivery state' }[view];
    downloadCsv(`online-store-by-${view}-${range[0]}-to-${range[1]}.csv`,
      [title, 'Invoices', 'Sales (before GST)', 'Returned', 'Net sales', 'Share %'].concat(view === 'products' ? ['Quantity sold', 'Quantity returned'] : []),
      rows.map((r) => [r.name, r.invoices, r.sales, r.credited, r.net, r.share].concat(view === 'products' ? [r.quantity, r.returnedQuantity] : [])));
  };

  return (
    <div className="mt-3" data-testid="store-reports">
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <ButtonGroup size="sm">
          {PERIODS.map(([key, text]) => (
            <Button key={key} variant={preset === key ? 'secondary' : 'outline-secondary'} onClick={() => choose(key)}>{text}</Button>
          ))}
        </ButtonGroup>
        <div className="d-flex align-items-center gap-1 ms-md-2">
          <DateInput size="sm" value={range[0]} max={range[1]} style={{ width: 'auto' }} onChange={(e) => { setPreset(''); setRange([e.target.value, range[1]]); }} />
          <span className="small text-muted">to</span>
          <DateInput size="sm" value={range[1]} min={range[0]} style={{ width: 'auto' }} onChange={(e) => { setPreset(''); setRange([range[0], e.target.value]); }} />
        </div>
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      {data && (
        <>
          <Row className="g-3 mb-3">
            {[
              ['Orders placed', `${o.placed}${o.averageOrder ? ` · avg ${money(o.averageOrder)}` : ''}`],
              ['Ordered value', money(o.value)],
              ['Net sales (before GST)', money(s.net)],
              ['Invoiced', `${s.invoices} · ${money(s.taxable)}`],
              ['Sent back', `${s.creditNotes} · ${money(s.credited)}`],
              ['Buyers', o.buyers]
            ].map(([text, value]) => (
              <Col key={text} xs={6} md={4} lg>
                <Card className="text-center h-100"><Card.Body className="py-2">
                  <div className="fs-5 fw-semibold">{value}</div>
                  <div className="small text-muted">{text}</div>
                </Card.Body></Card>
              </Col>
            ))}
          </Row>
          {(o.cancelled > 0 || o.returned > 0 || num(s.delivery) > 0) && (
            <Alert variant="light" className="small py-2" data-testid="store-report-notes">
              {o.cancelled > 0 && <>{o.cancelled} of the orders placed ({money(o.cancelledValue)}) {o.cancelled === 1 ? 'was' : 'were'} cancelled. </>}
              {o.returned > 0 && <>{o.returned} {o.returned === 1 ? 'order has' : 'orders have'} been returned in full. </>}
              {num(s.delivery) > 0 && <>Delivery charges collected: {money(s.delivery)}.</>}
            </Alert>
          )}

          <Card className="mb-3">
            <Card.Header className="py-2"><strong>{data.grouping === 'day' ? 'Day by day' : 'Month by month'}</strong></Card.Header>
            <Card.Body style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.periods.map((p) => ({ ...p, label: label(p.key), orderedValue: num(p.orderedValue), net: num(p.net) }))} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.axis }} tickLine={false} axisLine={{ stroke: colors.grid }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 12, fill: colors.axis }} axisLine={false} tickLine={false} width={70} tickFormatter={shortAmount} />
                  <Tooltip formatter={(v, name) => [money(v), name === 'orderedValue' ? 'Ordered' : 'Net sales']} />
                  <Legend formatter={(v) => (v === 'orderedValue' ? 'Ordered' : 'Net sales (invoiced less returns)')} />
                  <Bar dataKey="orderedValue" fill={colors.ordered} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="net" fill={colors.net} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <ButtonGroup size="sm">
              <Button variant={view === 'products' ? 'primary' : 'outline-primary'} onClick={() => setView('products')}>By product</Button>
              <Button variant={view === 'methods' ? 'primary' : 'outline-primary'} onClick={() => setView('methods')}>By payment method</Button>
              <Button variant={view === 'states' ? 'primary' : 'outline-primary'} onClick={() => setView('states')}>By state</Button>
            </ButtonGroup>
            <Button size="sm" variant="outline-secondary" className="ms-auto" disabled={rows.length === 0} onClick={exportRows}><i className="bi bi-download me-1" />Download CSV</Button>
          </div>
          <Table striped bordered hover responsive size="sm" className="small">
            <thead>
              <tr>
                <th>{{ products: 'Product', methods: 'Payment method', states: 'Delivery state' }[view]}</th>
                {view === 'products' && <th className="text-end">Sold</th>}
                {view !== 'products' && <th className="text-end">Invoices</th>}
                <th className="text-end">Sales</th><th className="text-end">Returned</th><th className="text-end">Net sales</th><th className="text-end">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">No online sales in this period.</td></tr>}
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.name}</td>
                  {view === 'products' && <td className="text-end text-nowrap">{r.quantity ? `${Number(r.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${r.unit || ''}` : '-'}{r.returnedQuantity && <span className="text-danger"> (−{Number(r.returnedQuantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })})</span>}</td>}
                  {view !== 'products' && <td className="text-end">{r.invoices}</td>}
                  <td className="text-end text-nowrap">{money(r.sales)}</td>
                  <td className="text-end text-nowrap">{num(r.credited) > 0 ? money(r.credited) : '-'}</td>
                  <td className="text-end text-nowrap">{money(r.net)}</td>
                  <td className="text-end">{share(r.share)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="small text-muted">
            Sales are the tax invoices made for online orders, before GST, by the invoice date. Returns are the credit notes dated in the period, so a return can fall in a later
            period than its sale. Orders placed are counted by the day they were placed, whether or not they have been invoiced yet.
          </p>
        </>
      )}
    </div>
  );
}

export default StoreReports;
